import * as React from 'react'
import moment from 'moment'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useDate } from '@/modules/drivers/stores/dateStore'
import useBusinessTripService from '../services/BusinessTripService'
import { BusinessTripModel } from '@/app/models/businessTripModel'
import Group from '../tabs/businessTrips/views/Group'
import { format, addMonths, subMonths } from 'date-fns'
import { TableProvider } from '../context/TableContext'
import { DatePickerRangeProps } from '../types'

import { DateRangeFilter } from '@/app/constants/delegation/dateRangeFilter'
import { SERVICE_DATE_FORMAT } from '@/app/constants/date'
import { generatePath } from 'react-router-dom'
import { driversRoutesMap } from '../routes'
import { useSideSelectionStore } from '../stores/sideSelectionStore'

const GroupBusinessTripsContent = ({
  match,
  history,
  companyId,
}: {
  match: any
  history: any
  companyId: string
}) => {
  const { range, setRange, getDateRangeForView } = useDate()
  const { getBusinessTrips } = useBusinessTripService()
  const { basicDrivers } = useDrivers()
  const { sideCheckedIds } = useSideSelectionStore()
  const [trips, setTrips] = React.useState<BusinessTripModel[]>([])
  const [dateRangeFilter, setDateRangeFilter] = React.useState<DateRangeFilter>(
    DateRangeFilter.Started,
  )
  const [polishOnlyFilter, setPolishOnlyFilter] = React.useState<boolean>(false)

  const [refreshKey, setRefreshKey] = React.useState(0)

  const handleRefresh = React.useCallback(() => {
    setRefreshKey((prev) => prev + 1)
  }, [])

  React.useEffect(() => {
    window.addEventListener('businessTrips:refresh', handleRefresh)
    return () => {
      window.removeEventListener('businessTrips:refresh', handleRefresh)
    }
  }, [handleRefresh])
  const { params } = match
  const { from, to } = params

  const convertToDatePickerProps = (
    dateString: string | undefined,
  ): DatePickerRangeProps['startDate'] | null => {
    if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return null
    }
    const parsed = moment(dateString, SERVICE_DATE_FORMAT, true)
    if (!parsed.isValid()) {
      return null
    }
    const date = parsed.toDate()
    if (isNaN(date.getTime())) {
      return null
    }
    return {
      fullDate: format(date, 'DD-MM-YYYY'),
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    }
  }

  React.useEffect(() => {
    if (from && to) {
      const start = convertToDatePickerProps(from)
      const end = convertToDatePickerProps(to)
      if (start && end) {
        const newRange = { startDate: start, endDate: end }
        if (
          !range ||
          range.startDate?.fullDate !== newRange.startDate?.fullDate ||
          range.endDate?.fullDate !== newRange.endDate?.fullDate
        ) {
          setRange(newRange)
        }
      }
    } else if (!range) {
      const { from: defaultFrom, to: defaultTo } = getDateRangeForView()
      const fromDate = new Date(defaultFrom)
      const toDate = new Date(defaultTo)

      setRange({
        startDate: {
          fullDate: format(fromDate, 'dd-MM-yy'),
          year: fromDate.getFullYear(),
          month: fromDate.getMonth() + 1,
          day: fromDate.getDate(),
        },
        endDate: {
          fullDate: format(toDate, 'dd-MM-yy'),
          year: toDate.getFullYear(),
          month: toDate.getMonth() + 1,
          day: toDate.getDate(),
        },
      })
    }
  }, [from, to, setRange, range])

  React.useEffect(() => {
    const fetchTrips = async () => {
      if (!from || !to || !range?.startDate || !range?.endDate) return

      const driverIdsToFetch = sideCheckedIds.length > 0 ? sideCheckedIds : []

      if (driverIdsToFetch.length === 0) {
        setTrips([])
        return
      }

      try {
        const startDate = new Date(
          range.startDate.year!,
          range.startDate.month! - 1,
          range.startDate.day!,
        )
        const endDate = new Date(
          range.endDate.year!,
          range.endDate.month! - 1,
          range.endDate.day!,
        )

        const fetchStart = subMonths(startDate, 6)
        const fetchEnd = addMonths(endDate, 2)

        const result = await getBusinessTrips({
          companyId,
          drivers: driverIdsToFetch,
          startTime: fetchStart,
          endTime: fetchEnd,
          filter: DateRangeFilter.Started,
          onlyPolish: polishOnlyFilter,
        })
        setTrips(result || [])
      } catch (error) {
        console.error('Error fetching grouped business trips:', error)
        setTrips([])
      }
    }

    fetchTrips()
  }, [
    from,
    to,
    range?.startDate?.fullDate,
    range?.endDate?.fullDate,
    sideCheckedIds.join(','),
    basicDrivers.length,
    companyId,
    polishOnlyFilter,
    refreshKey,
  ])

  const filteredTrips = React.useMemo(() => {
    if (!from || !to || trips.length === 0) return trips

    const rangeStart = moment(from, 'YYYY-MM-DD').startOf('day')
    const rangeEnd = moment(to, 'YYYY-MM-DD').endOf('day')

    if (!rangeStart.isValid() || !rangeEnd.isValid()) return trips

    return trips.filter((trip) => {
      switch (dateRangeFilter) {
        case DateRangeFilter.Started: {
          const d = trip.startDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }
        case DateRangeFilter.Ended: {
          const d = trip.endDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }

        case DateRangeFilter.All:
        case DateRangeFilter.AllButSettlementMonth:
          return true
        case DateRangeFilter.Modified: {
          const d = trip.modificationDate
          if (!d) return true
          const m = moment.isMoment(d) ? d : moment(d)
          return m.isSameOrAfter(rangeStart) && m.isSameOrBefore(rangeEnd)
        }
        default:
          return true
      }
    })
  }, [trips, from, to, dateRangeFilter])
  const driverIdsToUse =
    sideCheckedIds.length > 0 ? sideCheckedIds.map(String) : []

  return (
    <Group
      trips={filteredTrips}
      companyId={companyId}
      driverIds={driverIdsToUse}
      dateRangeFilter={dateRangeFilter}
      onDateRangeFilterChange={setDateRangeFilter}
      allDrivers={
        driverIdsToUse.length > 0
          ? basicDrivers.filter((d) => driverIdsToUse.includes(String(d.id)))
          : []
      }
      match={match}
      history={history}
      handleRefresh={handleRefresh}
    />
  )
}

export const DriverGroupBusinessTripsPage = ({ match, history }: any) => {
  const { params } = match
  const { companyId, from, to } = params

  React.useEffect(() => {
    if (companyId && (!from || !to || from.trim() === '' || to.trim() === '')) {
      const today = new Date()
      const oneMonthAgo = subMonths(today, 1)
      const fromDate = format(oneMonthAgo, 'YYYY-MM-DD')
      const toDate = format(today, 'YYYY-MM-DD')
      const newPath = generatePath(driversRoutesMap.driverGroupBusinessTrips, {
        companyId,
        from: fromDate,
        to: toDate,
      })
      history.replace(newPath)
    }
  }, [companyId, from, to, history])

  return (
    <section className="tw-flex tw-h-full tw-w-full tw-flex-col">
      <TableProvider bulk={true}>
        <GroupBusinessTripsContent
          match={match}
          history={history}
          companyId={companyId}
        />
      </TableProvider>
    </section>
  )
}
